// Remembers scroll position per pathname inside the current tab session.
// Used so that returning from a product detail page lands the user exactly
// where they were inside a long product grid (Shop / ShopWomen).

const KEY_Y = 'scrollY:';
const KEY_H = 'scrollH:';
const RETURN_KEY = 'shop:returnLocation';
const RESTORE_KEY = 'shop:restoreRoute';
const MEMORY_PATHS = new Set<string>(['/shop-men', '/shop-women']);

export type ShopReturnLocation = {
  pathname: string;
  search: string;
  y: number;
  height: number;
  productHandle?: string;
  productViewportTop?: number;
};

function routePath(routeKey: string): string {
  return routeKey.split('?')[0];
}

export function makeRouteKey(pathname: string, search = ''): string {
  return `${pathname}${search}`;
}

export function isMemoryPath(path: string): boolean {
  return MEMORY_PATHS.has(routePath(path));
}

export function saveScroll(path: string, y: number, height: number): void {
  try {
    sessionStorage.setItem(KEY_Y + path, String(Math.max(0, Math.round(y))));
    sessionStorage.setItem(KEY_H + path, String(Math.round(height)));
  } catch { /* sessionStorage unavailable */ }
}

export function readScroll(path: string): { y: number; height: number } | null {
  try {
    const y = sessionStorage.getItem(KEY_Y + path);
    if (y === null) return null;
    const h = sessionStorage.getItem(KEY_H + path);
    return { y: parseInt(y, 10) || 0, height: h ? parseInt(h, 10) || 0 : 0 };
  } catch { return null; }
}

export function clearScroll(path: string): void {
  try {
    sessionStorage.removeItem(KEY_Y + path);
    sessionStorage.removeItem(KEY_H + path);
  } catch { /* noop */ }
}

export function saveReturnLocation(location: ShopReturnLocation): void {
  try {
    if (!isMemoryPath(location.pathname)) return;
    const routeKey = makeRouteKey(location.pathname, location.search);
    saveScroll(routeKey, location.y, location.height);
    sessionStorage.setItem(RETURN_KEY, JSON.stringify(location));
  } catch { /* noop */ }
}

export function readReturnLocation(): ShopReturnLocation | null {
  try {
    const raw = sessionStorage.getItem(RETURN_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ShopReturnLocation>;
    if (!parsed.pathname || !isMemoryPath(parsed.pathname)) return null;
    return {
      pathname: parsed.pathname,
      search: parsed.search ?? '',
      y: Number(parsed.y) || 0,
      height: Number(parsed.height) || 0,
      productHandle: parsed.productHandle,
      productViewportTop: typeof parsed.productViewportTop === 'number' ? parsed.productViewportTop : undefined,
    };
  } catch { return null; }
}

export function requestScrollRestore(routeKey: string): void {
  try {
    sessionStorage.setItem(RESTORE_KEY, routeKey);
  } catch { /* noop */ }
}

export function hasPendingScrollRestore(routeKey: string): boolean {
  try {
    return sessionStorage.getItem(RESTORE_KEY) === routeKey;
  } catch { return false; }
}

export function consumePendingScrollRestore(routeKey: string): boolean {
  try {
    if (sessionStorage.getItem(RESTORE_KEY) !== routeKey) return false;
    sessionStorage.removeItem(RESTORE_KEY);
    return true;
  } catch { return false; }
}